/**
 * KCKBCK cart: the cart panel that slides in from the right, and the round "quick add" button on
 * product cards.
 *
 * Cart panel (<kck-mini-cart>, markup in sections/kck-mini-cart.liquid)
 * - Opens by itself when something is added to the cart, and when the header cart icon is clicked.
 *   It stays open until the shopper closes it (X, Escape, or a click on the dimmed page).
 * - Its content is server-rendered and re-fetched through the Section Rendering API after every
 *   cart change, so prices, discounts and currency always match the real cart.
 * - Quantity and "Eliminar" buttons call the Cart API directly.
 * - It listens to two "cart changed" signals:
 *     1. `kckbck:cart-update`, dispatched by the size selector on the product page and by the
 *        quick add button below.
 *     2. Shopify's standard cart event, dispatched by every theme component that adds to the cart.
 *
 * Quick add (<kck-quick-add>, markup in snippets/kck-quick-add.liquid)
 * - Shows the sizes of the product over the card photo; choosing one adds it to the cart.
 */

const TRIGGER_SELECTOR = 'a.action__cart';
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
const CART_UPDATE_EVENT = 'kckbck:cart-update';

/** @param {number} itemCount */
function updateCartBubble(itemCount) {
  document.querySelectorAll('cart-icon').forEach((icon) => {
    const cartIcon = /** @type {any} */ (icon);
    if (typeof cartIcon.renderCartBubble === 'function') cartIcon.renderCartBubble(itemCount);
  });
}

class KckMiniCart extends HTMLElement {
  /** Guards against an older response overwriting a newer one. */
  #sequence = 0;
  /** @type {HTMLElement | null} */
  #returnFocusTo = null;
  /** @type {{ overflow: string, paddingRight: string } | null} */
  #scrollLock = null;

  connectedCallback() {
    if (this.kckReady) return;
    this.kckReady = true;

    this.panel = /** @type {HTMLElement | null} */ (this.querySelector('[data-kck-panel]'));
    if (!this.panel || this.hasAttribute('data-disabled')) return;

    this.#triggers().forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      if (this.panel?.id) trigger.setAttribute('aria-controls', this.panel.id);
    });

    document.addEventListener('click', this.#onDocumentClick, true);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener(CART_UPDATE_EVENT, this.#onCustomAdd);
    window.addEventListener('pageshow', this.#onPageShow);
    this.addEventListener('click', this.#onPanelClick);

    // The standard cart event name lives in Shopify's events module. It is loaded on demand so a
    // problem with that module can never stop the cart panel itself from working.
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        if (StandardEvents?.cartLinesUpdate) {
          document.addEventListener(StandardEvents.cartLinesUpdate, this.#onStandardUpdate);
        }
      })
      .catch(() => {});

    // The theme's "open the cart" action sends shoppers to /cart when its own drawer is off.
    // Point it at this panel instead (after the theme has registered its version).
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.#takeOverOpenCart, { once: true });
    } else {
      this.#takeOverOpenCart();
    }
    window.addEventListener('load', this.#takeOverOpenCart, { once: true });

    // The cart icon was clicked before this script had loaded (see the inline script in the section).
    const pending = /** @type {any} */ (window);
    if (pending.kckCartOpenWhenReady) {
      pending.kckCartOpenWhenReady = false;
      this.open();
      this.refresh();
    }
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  /** @returns {HTMLElement[]} */
  #triggers() {
    return /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll(TRIGGER_SELECTOR)));
  }

  #takeOverOpenCart = () => {
    try {
      /** @type {any} */ (window).Shopify?.actions?.openCart?.configure({
        handler: async () => {
          await this.refresh();
          this.open();
        },
      });
    } catch (error) {
      console.warn('[kck-mini-cart] Could not take over the open cart action:', error);
    }
  };

  open() {
    if (!this.panel || this.isOpen) return;

    const active = document.activeElement;
    this.#returnFocusTo = active instanceof HTMLElement && active !== document.body ? active : null;

    this.#lockScroll();
    this.panel.inert = false;
    this.setAttribute('open', '');
    this.#triggers().forEach((el) => el.setAttribute('aria-expanded', 'true'));
    this.panel.focus({ preventScroll: true });
  }

  close() {
    if (!this.panel || !this.isOpen) return;

    this.removeAttribute('open');
    this.panel.inert = true;
    this.#triggers().forEach((el) => el.setAttribute('aria-expanded', 'false'));
    this.#unlockScroll();

    const target = this.#returnFocusTo?.isConnected ? this.#returnFocusTo : this.#triggers()[0];
    target?.focus({ preventScroll: true });
    this.#returnFocusTo = null;
  }

  /** Re-renders the cart content from the server. */
  async refresh() {
    const sequence = ++this.#sequence;
    const root = this.dataset.rootUrl || '/';
    const sectionId = this.dataset.sectionId || 'kck-mini-cart';
    const separator = root.includes('?') ? '&' : '?';

    try {
      const response = await fetch(`${root}${separator}section_id=${encodeURIComponent(sectionId)}`, {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Section request failed (${response.status})`);

      const html = await response.text();
      if (sequence !== this.#sequence) return;

      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-kck-content]');
      const current = this.querySelector('[data-kck-content]');
      if (!fresh || !current) return;

      const hadFocus = current.contains(document.activeElement);
      const scrollTop = current.querySelector('[data-kck-items]')?.scrollTop ?? 0;
      current.replaceWith(fresh);
      const items = this.querySelector('[data-kck-items]');
      if (items) items.scrollTop = scrollTop;

      // The focused button was just replaced: keep keyboard focus inside the open panel.
      if (hadFocus && this.isOpen) this.panel?.focus({ preventScroll: true });
    } catch (error) {
      console.warn('[kck-mini-cart] Could not refresh the cart:', error);
    }
  }

  /**
   * Changes the quantity of a line (0 removes it).
   * @param {string} key
   * @param {number} quantity
   */
  async change(key, quantity) {
    if (this.hasAttribute('busy')) return;
    this.setAttribute('busy', '');

    const changeUrl = this.dataset.changeUrl || '/cart/change';
    let errorMessage = '';

    try {
      const response = await fetch(`${changeUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity }),
      });
      const data = await response.json().catch(() => null);

      if (response.ok && data) {
        updateCartBubble(data.item_count);
      } else {
        errorMessage = data?.description || data?.message || this.dataset.errorMessage || '';
      }
    } catch (error) {
      errorMessage = this.dataset.errorMessage || '';
      console.warn('[kck-mini-cart] Could not update the cart:', error);
    }

    await this.refresh();
    this.removeAttribute('busy');

    if (errorMessage) {
      const errorElement = /** @type {HTMLElement | null} */ (this.querySelector('[data-kck-error]'));
      if (errorElement) {
        errorElement.textContent = errorMessage;
        errorElement.hidden = false;
      }
    }
  }

  /** Stops the page behind the panel from scrolling, without the layout jumping sideways. */
  #lockScroll() {
    if (this.#scrollLock) return;
    const root = document.documentElement;
    const scrollbarWidth = window.innerWidth - root.clientWidth;

    this.#scrollLock = { overflow: root.style.overflow, paddingRight: document.body.style.paddingRight };
    root.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  #unlockScroll() {
    if (!this.#scrollLock) return;
    document.documentElement.style.overflow = this.#scrollLock.overflow;
    document.body.style.paddingRight = this.#scrollLock.paddingRight;
    this.#scrollLock = null;
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const trigger = /** @type {HTMLElement | null} */ (target.closest(TRIGGER_SELECTOR));
    if (trigger) {
      // Leave "open in new tab" style clicks alone.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
        this.refresh();
      }
      return;
    }

    // The dimmed page behind the panel. Anything drawn above it (a cookie banner, for instance)
    // keeps working normally and does not close the cart.
    if (this.isOpen && target.closest('[data-kck-backdrop]')) this.close();
  };

  /** @param {MouseEvent} event */
  #onPanelClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-kck-close]')) {
      this.close();
      return;
    }

    const quantityButton = /** @type {HTMLElement | null} */ (target.closest('[data-kck-quantity]'));
    const line = /** @type {HTMLElement | null} */ (quantityButton?.closest('[data-kck-line]') ?? null);
    if (!quantityButton || !line?.dataset.key) return;

    const quantity = Number.parseInt(quantityButton.dataset.kckQuantity ?? '', 10);
    if (Number.isNaN(quantity) || quantity < 0) return;

    line.setAttribute('aria-busy', 'true');
    this.change(line.dataset.key, quantity);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!this.isOpen || !this.panel) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    // Keep Tab inside the panel while it is open.
    if (event.key !== 'Tab') return;
    const focusable = /** @type {HTMLElement[]} */ (Array.from(this.panel.querySelectorAll(FOCUSABLE_SELECTOR)));
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === this.panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    } else if (!this.panel.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    // Coming back with the browser's back button can restore a stale (and still open) page.
    if (!event.persisted) return;
    this.close();
    this.refresh();
  };

  #onCustomAdd = () => {
    this.refresh().then(() => this.open());
  };

  /** @param {any} event */
  #onStandardUpdate = (event) => {
    const isAdd = event.action === 'add';
    const modal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    const run = () =>
      this.refresh().then(() => {
        if (!isAdd) return;
        // A modal (quick add) sits above everything: wait for it to close first.
        if (modal instanceof HTMLDialogElement && modal.open) {
          modal.addEventListener('close', () => this.open(), { once: true });
        } else {
          this.open();
        }
      });

    if (event.promise && typeof event.promise.then === 'function') {
      event.promise
        .then((/** @type {any} */ result) => {
          if (!result?.detail?.didError) run();
        })
        .catch(() => {});
    } else {
      run();
    }
  };
}

class KckQuickAdd extends HTMLElement {
  /** @param {KckQuickAdd} [except] */
  static closeAll(except) {
    document.querySelectorAll('kck-quick-add[open]').forEach((element) => {
      if (element !== except && element instanceof KckQuickAdd) element.close();
    });
  }

  connectedCallback() {
    if (this.kckReady) return;
    this.kckReady = true;
    this.addEventListener('click', this.#onClick);
  }

  get sizesPanel() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-kck-qa-panel]'));
  }

  get toggle() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-kck-qa-toggle]'));
  }

  open() {
    const panel = this.sizesPanel;
    if (!panel) return;

    KckQuickAdd.closeAll(this);
    this.#showMessage('');
    panel.hidden = false;
    this.setAttribute('open', '');
    this.toggle?.setAttribute('aria-expanded', 'true');
  }

  close() {
    const panel = this.sizesPanel;
    if (!panel) return;

    panel.hidden = true;
    this.removeAttribute('open');
    this.toggle?.setAttribute('aria-expanded', 'false');
  }

  /**
   * @param {string} variantId
   * @param {HTMLElement} button
   */
  async add(variantId, button) {
    if (this.hasAttribute('busy')) return;
    this.setAttribute('busy', '');
    button.setAttribute('aria-busy', 'true');
    this.#showMessage('');

    const addUrl = this.dataset.addUrl || '/cart/add';
    const cartUrl = this.dataset.cartUrl || '/cart';

    try {
      const response = await fetch(`${addUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.description || data?.message || '');

      this.close();

      const cart = await fetch(`${cartUrl}.js`, { credentials: 'same-origin', cache: 'no-store' })
        .then((cartResponse) => cartResponse.json())
        .catch(() => null);
      if (cart) updateCartBubble(cart.item_count);

      // The cart panel listens to this and opens with the new item.
      document.dispatchEvent(
        new CustomEvent(CART_UPDATE_EVENT, { bubbles: true, detail: { cart, addedVariantId: variantId } })
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      this.#showMessage(message || this.dataset.errorMessage || '');
      // A single-variant product has no picker to show the message in.
      if (!this.sizesPanel && (message || this.dataset.errorMessage)) {
        console.warn('[kck-quick-add]', message || this.dataset.errorMessage);
      }
    } finally {
      button.removeAttribute('aria-busy');
      this.removeAttribute('busy');
    }
  }

  /** @param {string} text */
  #showMessage(text) {
    const message = /** @type {HTMLElement | null} */ (this.querySelector('[data-kck-qa-message]'));
    if (!message) return;
    message.textContent = text;
    message.hidden = text === '';
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Never let a tap on the picker reach the card (which would open the product page).
    event.stopPropagation();

    const variantButton = target.closest('[data-variant-id]');
    if (variantButton instanceof HTMLButtonElement) {
      event.preventDefault();
      if (!variantButton.disabled && variantButton.dataset.variantId) {
        this.add(variantButton.dataset.variantId, variantButton);
      }
      return;
    }

    if (target.closest('[data-kck-qa-close]')) {
      this.close();
      return;
    }

    if (target.closest('[data-kck-qa-toggle]')) {
      event.preventDefault();
      if (this.hasAttribute('open')) {
        this.close();
      } else {
        this.open();
      }
    }
  };
}

// One listener for every card: a click anywhere else, or Escape, closes an open size picker.
document.addEventListener('click', (event) => {
  if (event.target instanceof Element && event.target.closest('kck-quick-add')) return;
  KckQuickAdd.closeAll();
});
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') KckQuickAdd.closeAll();
});

if (!customElements.get('kck-mini-cart')) {
  customElements.define('kck-mini-cart', KckMiniCart);
}
if (!customElements.get('kck-quick-add')) {
  customElements.define('kck-quick-add', KckQuickAdd);
}
